import { useGSAP } from "@gsap/react";
import { Autocomplete, Popover, TextField } from "@mui/material";
import gsap from "gsap";
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiCrosshair,
  FiDollarSign,
  FiEdit3,
  FiMinus,
  FiPackage,
  FiPercent,
  FiPlus,
  FiSearch,
  FiShoppingCart,
  FiTrash2,
  FiX
} from "react-icons/fi";
import { SiPix } from "react-icons/si";
import { makeToasterErrorHandler } from "../../common/http/makeToasterErrorHandler";
import { ClientInterface, ProductInterface } from "../../common/interfaces";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomTooltip from "../../components/CustomTooltip";
import { TypeSaleType, TypeSaleTypeLabel } from "../../components/enum/saleEnums";
import ProductSearchModal from "../../components/modal/ProductSearchModal";
import Receipt from "../../components/receipt/Receipt";
import PageToaster from "../../components/toaster/PageToaster";
import { usePageToast } from "../../hooks/usePageToast";
import api from "../../services/api";
import {
  DeferredFieldsInner,
  DeferredFieldsWrap,
  DeferredHint,
  PaymentSection,
  PaymentSectionLabel,
  PaymentTypeIndicator,
  PaymentTypePill,
  PaymentTypeRow,
  ActionsRow,
  ApplyDiscountBtn,
  CancelSaleBtn,
  CartEmptyState,
  CartFooter,
  CartHeader,
  CartList,
  CartQtyStepper,
  CartRow,
  CartRowInfo,
  CartRowMeta,
  CartRowRemove,
  CartRowTotal,
  CartTitle,
  ClearCartBtn,
  ClearQueryBtn,
  CountBadge,
  DiscountChip,
  DiscountPopoverInputRow,
  DiscountPopoverLabel,
  DiscountPopoverPanel,
  DropdownState,
  EmptyState,
  Eyebrow,
  FinalizeBtn,
  IconGhostButton,
  LastAddedBadge,
  LastAddedCard,
  LastAddedIcon,
  LastAddedMeta,
  LastAddedName,
  LeftPanel,
  ModeButton,
  ModeSwitch,
  PanelBody,
  PanelHeader,
  PanelTitle,
  PopoverActionsRow,
  PresetChip,
  PresetRow,
  QtyStepper,
  RemoveDiscountLink,
  ResultInfo,
  ResultPrice,
  ResultRow,
  ResultsDropdown,
  RightPanel,
  SaleContainer,
  ScanBadge,
  SearchField,
  SearchInputEl,
  SearchWrap,
  Spinner,
  StockBadge,
  SummaryRow,
  TotalRow,
  TotalValue
} from "./sale.styled";

gsap.registerPlugin(useGSAP);

export interface SaleItem {
  product_id: number;
  product_name: string;
  quantity: number;
  unit_price: number;
  discount: number;
  subtotal: number;
  id: number;
  max_quantity?: number;
}

const fmtCurrency = (value: number): string =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const Sale: React.FC = () => {
  // Estados de busca e inserção
  const [productQuery, setProductQuery] = useState<string>("");
  const [results, setResults] = useState<ProductInterface[]>([]);
  const [resultQuantities, setResultQuantities] = useState<
    Record<number, number>
  >({});
  const [saleItems, setSaleItems] = useState<SaleItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [readType, setReadType] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(0);
  const [lastAdded, setLastAdded] = useState<
    (ProductInterface & { quantity: number }) | null
  >(null);
  const [lastAddedKey, setLastAddedKey] = useState<number>(0);
  const [discountAnchor, setDiscountAnchor] = useState<HTMLElement | null>(
    null
  );
  const [discountItemId, setDiscountItemId] = useState<number | null>(null);
  const [clearConfirmOpen, setClearConfirmOpen] = useState<boolean>(false);

  // Tipo de recebimento — só classifica a venda hoje, sem gateway nem
  // parcelamento de cartão (isso fica com a operadora).
  const [saleType, setSaleType] = useState<TypeSaleType>(TypeSaleType.CASH);
  const isDeferred = saleType === TypeSaleType.DEFERRED;

  // Cliente do cupom: busca no cadastro (nome/CPF/CNPJ) ou digitação livre
  // (freeSolo) para comprador avulso. Em venda A Prazo, precisa ser um
  // cliente cadastrado (com e-mail) — é obrigatório selecionar da lista.
  const [clientQuery, setClientQuery] = useState<string>("");
  const [clientOptions, setClientOptions] = useState<ClientInterface[]>([]);
  const [clientLoading, setClientLoading] = useState<boolean>(false);
  const [selectedClient, setSelectedClient] = useState<ClientInterface | null>(null);
  const [manualDocument, setManualDocument] = useState<string>("");
  const [dueDate, setDueDate] = useState<string>("");

  const searchRef = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const paymentRowRef = useRef<HTMLDivElement>(null);
  const paymentIndicatorRef = useRef<HTMLDivElement>(null);
  const paymentPillRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const deferredWrapRef = useRef<HTMLDivElement>(null);
  const deferredInnerRef = useRef<HTMLDivElement>(null);

  const containerId = "toaster-sale-page";
  const { autoSuccess, autoError } = usePageToast(containerId, true);

  const handleHttpError = makeToasterErrorHandler(autoError);

  // Modal de pesquisa completa
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  // Cupom de impressão
  const [showReceipt, setShowReceipt] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [lastSale, setLastSale] = useState<{
    saleNumber?: number;
    createdAt?: string;
    cashierName?: string;
    saleType?: TypeSaleType;
    clientName?: string;
    clientDocument?: string;
    dueDate?: string;
  }>({});

  // Recalcula total sempre que saleItems muda
  useEffect(() => {
    const t = saleItems.reduce((sum, item) => sum + item.subtotal, 0);
    setTotal(t);
  }, [saleItems]);

  const totalDiscount = saleItems.reduce(
    (sum, item) => sum + item.quantity * item.unit_price * (item.discount / 100),
    0
  );

  // Mesma regra do backend (SaleService/Utils.buildDefaultDueDate): uma
  // semana à frente ao meio-dia, empurrando fim de semana pra segunda. O
  // valor final sempre vem do que está no campo — o usuário pode ajustar.
  const buildDefaultDueDate = (): string => {
    const due = new Date();
    due.setDate(due.getDate() + 7);
    due.setHours(12, 0, 0, 0);
    const weekDay = due.getDay();
    if (weekDay === 6) due.setDate(due.getDate() + 2);
    if (weekDay === 0) due.setDate(due.getDate() + 1);

    const pad = (n: number) => String(n).padStart(2, "0");
    return `${due.getFullYear()}-${pad(due.getMonth() + 1)}-${pad(due.getDate())}`;
  };

  const selectSaleType = (type: TypeSaleType) => {
    setSaleType(type);
    if (type === TypeSaleType.DEFERRED && !dueDate) {
      setDueDate(buildDefaultDueDate());
    }
  };

  // Busca de cliente (nome/CPF/CNPJ) — mesmo padrão debounced já usado na
  // busca de produtos desta tela, e mesmo endpoint do autocomplete de
  // cliente em WorkOrderCreateModal.
  useEffect(() => {
    if (!clientQuery.trim()) {
      setClientOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setClientLoading(true);
      try {
        const res = await api.get<{ items: ClientInterface[] }>("/client", {
          params: { page: 1, limit: 8, search: clientQuery.trim() }
        });
        setClientOptions(res.data.items ?? []);
      } catch (err) {
        console.error("Erro ao buscar clientes:", err);
        setClientOptions([]);
      } finally {
        setClientLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [clientQuery]);

  // Indicador deslizante do seletor de tipo de recebimento — mede a pílula
  // ativa e anima até ela (Corporate: rápido, decisivo, sem overshoot). No
  // primeiro render só posiciona (gsap.set) — sem "crescer do zero" ao
  // abrir a tela.
  const paymentIndicatorMounted = useRef(false);
  useGSAP(
    () => {
      const pill = paymentPillRefs.current[saleType];
      const indicator = paymentIndicatorRef.current;
      if (!pill || !indicator) return;

      // offsetLeft/offsetWidth (não getBoundingClientRect): ambos medidos a
      // partir do padding-edge do PaymentTypeRow — o mesmo offsetParent do
      // indicador — então batem exatamente com o "left: 0" dele, sem
      // duplicar o padding do row num cálculo baseado em viewport.
      const x = pill.offsetLeft;
      const width = pill.offsetWidth;

      if (!paymentIndicatorMounted.current) {
        gsap.set(indicator, { x, width });
        paymentIndicatorMounted.current = true;
        return;
      }

      gsap.to(indicator, {
        x,
        width,
        duration: 0.28,
        ease: "power3.out"
      });
    },
    { dependencies: [saleType], revertOnUpdate: false }
  );

  // Expande/recolhe os campos de A Prazo (cliente obrigatório + data
  // prevista) com altura+opacidade animadas — entrada mais longa que a
  // saída (o usuário reage mais rápido a algo sumindo do que aparecendo).
  useGSAP(
    () => {
      const wrap = deferredWrapRef.current;
      const inner = deferredInnerRef.current;
      if (!wrap || !inner) return;

      if (isDeferred) {
        const targetHeight = inner.scrollHeight;
        gsap.to(wrap, {
          height: targetHeight,
          opacity: 1,
          duration: 0.32,
          ease: "power2.out"
        });
      } else {
        gsap.to(wrap, {
          height: 0,
          opacity: 0,
          duration: 0.22,
          ease: "power2.in"
        });
      }
    },
    { dependencies: [isDeferred], revertOnUpdate: false }
  );

  // Função de busca (código de barras ou nome)
  const handleSearch = async (
    productDataQuery: string
  ): Promise<ProductInterface[]> => {
    if (!productDataQuery.trim()) {
      setResults([]);
      setIsDropdownOpen(false);
      return [];
    }

    setLoading(true);
    setIsDropdownOpen(true);

    try {
      let items: ProductInterface[] = [];

      if (readType) {
        // Modo leitor: /products/barcode
        const res = await api.get<ProductInterface | ProductInterface[]>(
          "/products/barcode",
          { params: { product_barcode: productDataQuery } }
        );
        const raw = res.data;
        items = Array.isArray(raw) ? raw : [raw];
      } else {
        // Modo manual: /products?search=
        const res = await api.get<{ items: ProductInterface[] }>("/products", {
          params: { page: 1, limit: 8, search: productDataQuery.trim() }
        });
        items = res.data.items;
      }

      setResults(items);
      const qts: Record<number, number> = {};
      items.forEach((p) => {
        qts[p.product_id] = 1;
      });
      setResultQuantities(qts);
      setHighlightedIndex(0);

      return items;
    } catch (err) {
      console.error("Erro na busca de produtos:", err);
      setResults([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Insere produto no carrinho
  const handleInsert = (prod: ProductInterface, qtyOverride?: number) => {
    const qty = qtyOverride ?? resultQuantities[prod.product_id] ?? 1;
    const price = Number(prod.product_unit_price) || 0;

    const newItem: SaleItem = {
      product_id: prod.product_id,
      product_name: prod.product_name!,
      quantity: qty,
      unit_price: price,
      discount: 0,
      subtotal: qty * price,
      id: Math.random(),
      max_quantity: prod.product_quantity
    };

    setSaleItems((prev) => [...prev, newItem]);
    setLastAdded({ ...prod, quantity: qty });
    setLastAddedKey((k) => k + 1);

    // Limpa campo e resultados
    setResults([]);
    setProductQuery("");
    setIsDropdownOpen(false);
    setHighlightedIndex(0);
    searchRef.current?.focus();
  };

  const updateCartQuantity = (id: number, quantity: number) => {
    setSaleItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const max = item.max_quantity ?? Infinity;
        const clamped = Math.max(1, Math.min(quantity, max));
        return {
          ...item,
          quantity: clamped,
          subtotal: clamped * item.unit_price * (1 - item.discount / 100)
        };
      })
    );
  };

  const updateCartDiscount = (id: number, discount: number) => {
    setSaleItems((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const clamped = Math.max(0, Math.min(discount, 100));
        return {
          ...item,
          discount: clamped,
          subtotal: item.quantity * item.unit_price * (1 - clamped / 100)
        };
      })
    );
  };

  const removeCartItem = (id: number) => {
    setSaleItems((prev) => prev.filter((item) => item.id !== id));
  };

  const openDiscountPopover = (
    e: React.MouseEvent<HTMLElement>,
    itemId: number
  ) => {
    setDiscountAnchor(e.currentTarget);
    setDiscountItemId(itemId);
  };

  const closeDiscountPopover = () => {
    setDiscountAnchor(null);
    setDiscountItemId(null);
  };

  const discountEditingItem = saleItems.find(
    (item) => item.id === discountItemId
  );

  const handleClearSale = () => {
    if (saleItems.length === 0) return;
    setClearConfirmOpen(true);
  };

  const resetPaymentFields = () => {
    setSaleType(TypeSaleType.CASH);
    setSelectedClient(null);
    setClientQuery("");
    setClientOptions([]);
    setManualDocument("");
    setDueDate("");
  };

  const confirmClearSale = () => {
    setSaleItems([]);
    setLastAdded(null);
    setClearConfirmOpen(false);
    resetPaymentFields();
  };

  // Finaliza venda e exibe cupom
  const handleSale = async () => {
    if (saleItems.length === 0) {
      autoError("Nenhum item na venda!");
      return;
    }

    if (isDeferred && !selectedClient) {
      autoError("Venda a prazo exige selecionar um cliente cadastrado (precisamos do e-mail para avisar no vencimento).");
      return;
    }

    const manualName = !selectedClient ? clientQuery.trim() : "";
    const manualDoc = !selectedClient ? manualDocument.trim() : "";

    const payload = {
      items: saleItems.map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
        discount: item.discount
      })),
      sale_type: saleType,
      ...(selectedClient ? { client_id: selectedClient.client_id } : {}),
      ...(manualName ? { client_name: manualName } : {}),
      ...(manualDoc ? { client_document: manualDoc } : {}),
      ...(isDeferred ? { due_date: dueDate || buildDefaultDueDate() } : {})
    };

    setLoading(true);
    try {
      const res = await api.post<{
        sale_number?: number;
        created_at?: string;
        user?: { user_name?: string };
        sale_type?: TypeSaleType;
        due_date?: string;
        client_name?: string;
        client_document?: string;
      }>("/sales", payload);

      autoSuccess("Venda realizada com sucesso!");
      setLastSale({
        saleNumber: res.data?.sale_number,
        createdAt: res.data?.created_at,
        cashierName: res.data?.user?.user_name,
        saleType: res.data?.sale_type,
        clientName: res.data?.client_name ?? undefined,
        clientDocument: res.data?.client_document ?? undefined,
        dueDate: res.data?.due_date ?? undefined
      });
      setShowReceipt(true);
      // A limpeza do carrinho (e dos campos de pagamento) ocorre após a
      // impressão, via onAfterPrint.
    } catch (err) {
      console.error("Erro ao finalizar venda:", err);
      handleHttpError(err);
    } finally {
      setLoading(false);
    }
  };

  // Debounce no modo manual
  useEffect(() => {
    if (!readType) {
      const timer = setTimeout(() => {
        handleSearch(productQuery);
      }, 400);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productQuery, readType]);

  // Ao trocar modo, limpa campos
  useEffect(() => {
    setProductQuery("");
    setResults([]);
    setResultQuantities({});
    setIsDropdownOpen(false);
    setHighlightedIndex(0);
    searchRef.current?.focus();
  }, [readType]);

  // Fecha o dropdown ao clicar fora
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Atalhos globais: F2 foca a busca, Alt+S finaliza, Alt+Q cancela
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "F2") {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.altKey && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (!loading && saleItems.length > 0) handleSale();
      } else if (e.altKey && e.key.toLowerCase() === "q") {
        e.preventDefault();
        handleClearSale();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, saleItems]);

  const onSearchKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (results.length > 0) {
        setIsDropdownOpen(true);
        setHighlightedIndex((i) => Math.min(i + 1, results.length - 1));
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Escape") {
      setProductQuery("");
      setResults([]);
      setIsDropdownOpen(false);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (readType) {
        const encontrados = await handleSearch(productQuery);
        if (encontrados.length > 0) handleInsert(encontrados[0]);
      } else if (isDropdownOpen && results.length > 0) {
        handleInsert(results[highlightedIndex] ?? results[0]);
      } else {
        await handleSearch(productQuery);
      }
    }
  };

  const bumpResultQty = (productId: number, max: number, delta: number) => {
    setResultQuantities((q) => ({
      ...q,
      [productId]: Math.max(1, Math.min(max, (q[productId] ?? 1) + delta))
    }));
  };

  return (
    <>
      <PageToaster containerId={containerId} />
      <SaleContainer>
      {/* ===== LEFT PANEL — busca e inserção de produtos ===== */}
      <LeftPanel>
        <PanelHeader>
          <div>
            <Eyebrow>PDV</Eyebrow>
            <PanelTitle>Buscar produto</PanelTitle>
          </div>

          <ModeSwitch>
            <ModeButton $active={!readType} onClick={() => setReadType(false)}>
              <FiEdit3 /> Manual
            </ModeButton>
            <ModeButton $active={readType} onClick={() => setReadType(true)}>
              <FiCrosshair /> Leitor
            </ModeButton>
          </ModeSwitch>
        </PanelHeader>

        <SearchWrap ref={wrapRef}>
          <SearchField $scanning={readType}>
            <FiSearch />
            <SearchInputEl
              ref={searchRef}
              autoFocus
              placeholder={
                readType
                  ? "Aguardando leitura do código de barras..."
                  : "Pesquise por nome ou código de barras (F2)"
              }
              value={productQuery}
              onChange={(e) => {
                setProductQuery(e.target.value);
                setHighlightedIndex(0);
              }}
              onFocus={() => {
                if (results.length > 0) setIsDropdownOpen(true);
              }}
              onKeyDown={onSearchKeyDown}
            />
            {readType && <ScanBadge>LEITOR ATIVO</ScanBadge>}
            {productQuery && (
              <ClearQueryBtn
                onClick={() => {
                  setProductQuery("");
                  setResults([]);
                  setIsDropdownOpen(false);
                  searchRef.current?.focus();
                }}
              >
                <FiX />
              </ClearQueryBtn>
            )}
          </SearchField>

          <CustomTooltip title="Pesquisa avançada">
            <IconGhostButton onClick={() => setIsModalOpen(true)}>
              <FiPackage />
            </IconGhostButton>
          </CustomTooltip>

          {isDropdownOpen && (
            <ResultsDropdown translate="no">
              {loading && (
                <DropdownState>
                  <Spinner /> Buscando produtos...
                </DropdownState>
              )}

              {!loading && results.length === 0 && (
                <DropdownState>
                  Nenhum produto encontrado{productQuery ? ` para "${productQuery}"` : ""}.
                </DropdownState>
              )}

              {!loading &&
                results.map((prod, idx) => {
                  const qty = resultQuantities[prod.product_id] ?? 1;
                  const price = Number(prod.product_unit_price) || 0;
                  const low = prod.product_quantity <= 5;

                  return (
                    <ResultRow
                      key={prod.product_id}
                      $active={idx === highlightedIndex}
                      onMouseEnter={() => setHighlightedIndex(idx)}
                      onClick={() => handleInsert(prod, qty)}
                    >
                      <ResultInfo>
                        <strong>{prod.product_name}</strong>
                        <span>{prod.product_barcode ?? "Sem código de barras"}</span>
                      </ResultInfo>

                      <StockBadge $low={low}>
                        {prod.product_quantity} un.
                      </StockBadge>

                      <QtyStepper onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          disabled={qty <= 1}
                          onClick={() =>
                            bumpResultQty(prod.product_id, prod.product_quantity, -1)
                          }
                        >
                          <FiMinus />
                        </button>
                        <span>{qty}</span>
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          disabled={qty >= prod.product_quantity}
                          onClick={() =>
                            bumpResultQty(prod.product_id, prod.product_quantity, 1)
                          }
                        >
                          <FiPlus />
                        </button>
                      </QtyStepper>

                      <ResultPrice>{fmtCurrency(price)}</ResultPrice>
                    </ResultRow>
                  );
                })}
            </ResultsDropdown>
          )}
        </SearchWrap>

        <PanelBody>
          {lastAdded ? (
            <LastAddedCard key={lastAddedKey}>
              <LastAddedBadge>
                <FiCheckCircle /> Adicionado ao carrinho
              </LastAddedBadge>
              <LastAddedIcon>
                <FiPackage />
              </LastAddedIcon>
              <LastAddedName>{lastAdded.product_name}</LastAddedName>
              <LastAddedMeta>
                <span>
                  Qtd.: <b>{lastAdded.quantity}</b>
                </span>
                <span>
                  Unitário:{" "}
                  <b>{fmtCurrency(Number(lastAdded.product_unit_price) || 0)}</b>
                </span>
                <span>Código: {lastAdded.product_barcode ?? "—"}</span>
              </LastAddedMeta>
            </LastAddedCard>
          ) : (
            <EmptyState>
              <div className="icon-wrap">
                <FiShoppingCart />
              </div>
              <h3>Comece uma nova venda</h3>
              <p>
                Digite o nome ou código de barras do produto na busca acima
                para adicioná-lo à venda.
              </p>
              <div className="hint-row">
                <kbd>Enter</kbd> adiciona
                <kbd>↑ ↓</kbd> navega
                <kbd>F2</kbd> busca
              </div>
            </EmptyState>
          )}
        </PanelBody>
      </LeftPanel>

      {/* ===== RIGHT PANEL — carrinho da venda ===== */}
      <RightPanel>
        <CartHeader>
          <CartTitle>
            <FiShoppingCart />
            <h2>Carrinho</h2>
            {saleItems.length > 0 && <CountBadge>{saleItems.length}</CountBadge>}
          </CartTitle>

          <CustomTooltip title="Cancelar venda (Alt+Q)">
            <ClearCartBtn
              disabled={saleItems.length === 0}
              onClick={handleClearSale}
            >
              <FiTrash2 />
            </ClearCartBtn>
          </CustomTooltip>
        </CartHeader>

        {saleItems.length === 0 ? (
          <CartEmptyState>
            <div className="icon-wrap">
              <FiShoppingCart />
            </div>
            <h4>Nenhum item na venda</h4>
            <p>Os produtos adicionados aparecerão aqui.</p>
          </CartEmptyState>
        ) : (
          <CartList>
            {saleItems.map((item) => (
              <CartRow key={item.id}>
                <CartRowInfo>
                  <strong>{item.product_name}</strong>
                  <CartRowMeta>
                    <span>{fmtCurrency(item.unit_price)} / un.</span>

                    <DiscountChip
                      type="button"
                      $hasDiscount={item.discount > 0}
                      onClick={(e) => openDiscountPopover(e, item.id)}
                    >
                      <FiPercent size={10} />
                      {item.discount > 0 ? `${item.discount}%` : "desconto"}
                    </DiscountChip>
                  </CartRowMeta>
                </CartRowInfo>

                <CartQtyStepper>
                  <button
                    type="button"
                    disabled={item.quantity <= 1}
                    onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                  >
                    <FiMinus />
                  </button>
                  <input
                    type="number"
                    value={item.quantity}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) =>
                      updateCartQuantity(item.id, Number(e.target.value) || 1)
                    }
                  />
                  <button
                    type="button"
                    disabled={
                      item.max_quantity != null &&
                      item.quantity >= item.max_quantity
                    }
                    onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                  >
                    <FiPlus />
                  </button>
                </CartQtyStepper>

                <CartRowTotal>{fmtCurrency(item.subtotal)}</CartRowTotal>

                <CartRowRemove onClick={() => removeCartItem(item.id)}>
                  <FiTrash2 />
                </CartRowRemove>
              </CartRow>
            ))}
          </CartList>
        )}

        <PaymentSection>
          <PaymentSectionLabel>Tipo de recebimento</PaymentSectionLabel>

          <PaymentTypeRow ref={paymentRowRef}>
            <PaymentTypeIndicator ref={paymentIndicatorRef} />

            {([
              { type: TypeSaleType.CASH, icon: <FiDollarSign /> },
              { type: TypeSaleType.PIX, icon: <SiPix /> },
              { type: TypeSaleType.CARD, icon: <FiCreditCard /> },
              { type: TypeSaleType.DEFERRED, icon: <FiClock /> }
            ] as const).map(({ type, icon }) => (
              <PaymentTypePill
                key={type}
                type="button"
                ref={(el) => {
                  paymentPillRefs.current[type] = el;
                }}
                $active={saleType === type}
                onClick={() => selectSaleType(type)}
              >
                {icon}
                {TypeSaleTypeLabel[type]}
              </PaymentTypePill>
            ))}
          </PaymentTypeRow>

          <Autocomplete<ClientInterface, false, false, true>
            freeSolo
            size="small"
            options={clientOptions}
            loading={clientLoading}
            value={selectedClient}
            inputValue={clientQuery}
            filterOptions={(opts) => opts}
            getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.client_name)}
            isOptionEqualToValue={(opt, val) =>
              typeof val !== "string" && opt.client_id === val.client_id
            }
            onInputChange={(_, newInputValue) => {
              setClientQuery(newInputValue);
              if (selectedClient && newInputValue !== selectedClient.client_name) {
                setSelectedClient(null);
              }
            }}
            onChange={(_, newValue) => {
              if (newValue && typeof newValue !== "string") {
                setSelectedClient(newValue);
                setClientQuery(newValue.client_name);
                setManualDocument(newValue.client_cnpj || newValue.client_cpf || "");
              } else {
                setSelectedClient(null);
              }
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.client_id}>
                {option.client_name}
                {option.client_cnpj || option.client_cpf
                  ? ` — ${option.client_cnpj || option.client_cpf}`
                  : ""}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                sx={{ mt: 1.5 }}
                label={isDeferred ? "Cliente (obrigatório)" : "Cliente no cupom (opcional)"}
                placeholder="Nome, CPF ou CNPJ"
              />
            )}
          />

          <TextField
            fullWidth
            size="small"
            sx={{ mt: 1 }}
            label="CPF/CNPJ no cupom"
            placeholder="Só se não achar o cliente acima"
            value={manualDocument}
            disabled={!!selectedClient}
            onChange={(e) => setManualDocument(e.target.value)}
          />

          <DeferredFieldsWrap ref={deferredWrapRef}>
            <DeferredFieldsInner ref={deferredInnerRef}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Pagamento previsto para"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <DeferredHint>
                Vamos avisar por e-mail e pelo sino do sistema quando essa data chegar,
                pedindo a confirmação do recebimento.
              </DeferredHint>
            </DeferredFieldsInner>
          </DeferredFieldsWrap>
        </PaymentSection>

        <CartFooter>
          {totalDiscount > 0 && (
            <SummaryRow>
              <span>Descontos aplicados</span>
              <b>-{fmtCurrency(totalDiscount)}</b>
            </SummaryRow>
          )}

          <TotalRow>
            <span>Total</span>
            <TotalValue>{fmtCurrency(total)}</TotalValue>
          </TotalRow>

          <ActionsRow>
            <CancelSaleBtn
              disabled={saleItems.length === 0}
              onClick={handleClearSale}
            >
              Cancelar
            </CancelSaleBtn>
            <FinalizeBtn
              onClick={handleSale}
              disabled={loading || saleItems.length === 0}
            >
              {loading ? <Spinner /> : <FiCheckCircle />}
              Finalizar venda
            </FinalizeBtn>
          </ActionsRow>
        </CartFooter>
      </RightPanel>

      {/* Popover de desconto do item */}
      <Popover
        open={Boolean(discountAnchor) && Boolean(discountEditingItem)}
        anchorEl={discountAnchor}
        onClose={closeDiscountPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "16px",
              border: "1px solid var(--border-strong)",
              boxShadow: "var(--shadow-lg)",
              backgroundColor: "var(--surface)",
              backgroundImage: "none",
              marginTop: "8px"
            }
          }
        }}
      >
        {discountEditingItem && (
          <DiscountPopoverPanel>
            <DiscountPopoverLabel>
              Desconto do item
              <strong>{discountEditingItem.product_name}</strong>
            </DiscountPopoverLabel>

            <DiscountPopoverInputRow>
              <input
                type="number"
                min={0}
                max={100}
                autoFocus
                value={discountEditingItem.discount}
                onFocus={(e) => e.target.select()}
                onChange={(e) =>
                  updateCartDiscount(discountEditingItem.id, Number(e.target.value))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") closeDiscountPopover();
                }}
              />
              <span>%</span>
            </DiscountPopoverInputRow>

            <PresetRow>
              {[5, 10, 15, 20].map((preset) => (
                <PresetChip
                  key={preset}
                  type="button"
                  $active={discountEditingItem.discount === preset}
                  onClick={() =>
                    updateCartDiscount(discountEditingItem.id, preset)
                  }
                >
                  {preset}%
                </PresetChip>
              ))}
            </PresetRow>

            <PopoverActionsRow>
              <RemoveDiscountLink
                type="button"
                disabled={discountEditingItem.discount === 0}
                onClick={() => updateCartDiscount(discountEditingItem.id, 0)}
              >
                Remover desconto
              </RemoveDiscountLink>
              <ApplyDiscountBtn type="button" onClick={closeDiscountPopover}>
                Aplicar
              </ApplyDiscountBtn>
            </PopoverActionsRow>
          </DiscountPopoverPanel>
        )}
      </Popover>

      {/* Modal de pesquisa completa */}
      <ProductSearchModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      {/* Confirmação de cancelamento da venda */}
      <ConfirmDialog
        open={clearConfirmOpen}
        title="Cancelar venda"
        description="Todos os itens adicionados ao carrinho serão removidos. Deseja continuar?"
        confirmText="Sim, cancelar"
        cancelText="Voltar"
        onClose={() => setClearConfirmOpen(false)}
        onConfirm={confirmClearSale}
      />

      {/* Cupom para impressão dentro de .receipt-wrapper */}
      {showReceipt &&
        ReactDOM.createPortal(
          <Receipt
            saleItems={saleItems.map((item) => ({
              product_name: item.product_name,
              quantity: item.quantity,
              unit_price: item.unit_price,
              subtotal: item.subtotal,
              discount: item.discount
            }))}
            total={total}
            saleNumber={lastSale.saleNumber}
            createdAt={lastSale.createdAt}
            cashierName={lastSale.cashierName}
            saleType={lastSale.saleType}
            clientName={lastSale.clientName}
            clientDocument={lastSale.clientDocument}
            dueDate={lastSale.dueDate}
            onAfterPrint={() => {
              setShowReceipt(false);
              setSaleItems([]);
              setLastAdded(null);
              setLastSale({});
              resetPaymentFields();
            }}
          />,
          document.body
        )}
      </SaleContainer>
    </>
  );
};

export default Sale;
